export type Lng = "en" | "da" | "sv";

export const supportedLngs: Lng[] = ["en", "da", "sv"];

export const isSupportedLng = (s: string | null | undefined): s is Lng =>
  s === "en" || s === "da" || s === "sv";

type PreferredLngSignals = {
  manual?: string | null;
  queryLang?: string | null;
  navigatorLanguage?: string | null;
  navigatorLanguages?: readonly string[] | null;
  timeZone?: string | null;
  countryCode?: string | null;
};

const regionLngs: Record<string, Lng> = {
  dk: "da",
  se: "sv",
};

const timeZoneLngs: Record<string, Lng> = {
  "Europe/Copenhagen": "da",
  "Europe/Stockholm": "sv",
};

const partsForLanguageTag = (value: string | null | undefined) =>
  (value ?? "").toLowerCase().split(/[-_]/).filter(Boolean);

const supportedLngFromPrimary = (value: string | null | undefined) => {
  const primary = partsForLanguageTag(value)[0];
  return isSupportedLng(primary) ? primary : null;
};

const supportedLngFromRegion = (value: string | null | undefined) => {
  const parts = partsForLanguageTag(value);
  const region = parts.find((part) => regionLngs[part]);
  return region ? regionLngs[region] : null;
};

const supportedLngFromCountry = (value: string | null | undefined) =>
  regionLngs[(value ?? "").toLowerCase()] ?? null;

const supportedLngFromTimeZone = (value: string | null | undefined) =>
  value ? timeZoneLngs[value] ?? null : null;

const uniqueLanguageTags = (signals: PreferredLngSignals) => {
  const tags = [
    ...(signals.navigatorLanguages ?? []),
    signals.navigatorLanguage,
  ].filter((value): value is string => Boolean(value));

  return Array.from(new Set(tags));
};

export function resolvePreferredLng(signals: PreferredLngSignals): Lng {
  if (isSupportedLng(signals.manual)) return signals.manual;
  if (isSupportedLng(signals.queryLang)) return signals.queryLang;

  const languageTags = uniqueLanguageTags(signals);
  const localLanguage = languageTags
    .map(supportedLngFromPrimary)
    .find((lng) => lng === "da" || lng === "sv");
  if (localLanguage) return localLanguage;

  const regionalLanguage = languageTags
    .map(supportedLngFromRegion)
    .find((lng): lng is Lng => Boolean(lng));
  if (regionalLanguage) return regionalLanguage;

  const countryLanguage = supportedLngFromCountry(signals.countryCode);
  if (countryLanguage) return countryLanguage;

  const timeZoneLanguage = supportedLngFromTimeZone(signals.timeZone);
  if (timeZoneLanguage) return timeZoneLanguage;

  const supportedLanguage = languageTags
    .map(supportedLngFromPrimary)
    .find((lng): lng is Lng => Boolean(lng));

  return supportedLanguage ?? "en";
}

const readLocalStorageLanguage = () => {
  try {
    return window.localStorage.getItem("lang");
  } catch {
    return null;
  }
};

const readQueryLanguage = () => {
  try {
    return new URLSearchParams(window.location.search).get("lang");
  } catch {
    return null;
  }
};

const readBrowserTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return null;
  }
};

export const detectPreferredLng = (): Lng => {
  if (typeof window === "undefined") return "en";

  return resolvePreferredLng({
    manual: readLocalStorageLanguage(),
    queryLang: readQueryLanguage(),
    navigatorLanguage: window.navigator.language,
    navigatorLanguages: window.navigator.languages,
    timeZone: readBrowserTimeZone(),
  });
};
